import { useCallback, useState } from 'react';
import { DateString, thisSundayDate, todayDate } from '../../../../../common/src/date_string.ts';
import {
  IntervalSongList,
  IntervalType,
  isDeletableSongListType,
  Org,
  OrgType,
  SongList,
  SongListEntryType,
  SongListSchema,
  SongListSchemaEntry,
  SongListType,
} from '../../../../../common/src/model.ts';
import { hasInterval, SongListChanges } from '../../../../../common/src/model_util.ts';
import { withCarriedPassedBlanks } from '../../../../../common/src/passed_sundays.ts';
import { LocalStorageKey } from '../../../data/client_local_storage.ts';
import { mapSongListId } from '../../../data/song_list_id_map.tsx';
import { recordSongListUpdate } from '../../../data/song_updates.ts';
import {
  addSongListSyncListener,
  removeSongListSyncListener,
  songListSyncWorkPending,
  synchronizeAllSongLists,
  useCurrentOrg,
} from '../../../data/use_current_org.tsx';
import { useLocalStorage } from '../../../data/use_local_storage.tsx';
import { useEffectOnce } from '../../../util/use_effect_once.ts';
import { normalizeChurchWeeklyListWrites, shiftSongListRows } from './song_list_rows.ts';

export interface AllSongLists {
  addCustomInterval({ start, end }: { start: DateString; end: DateString }): Promise<number>;
  deleteRowAt(rowIndex: number): Promise<void>;
  find(callback: (songList: SongList) => boolean): SongList | undefined;
  filter(callback: (songList: SongList) => boolean): SongList[];
  insertNewRowAt(rowIndex: number): Promise<void>;
  map<T>(callback: (songList: SongList) => T): T[];
  remove(songList: SongList): Promise<void>;
  toArray(): SongList[]; // every song list, for a pure function that takes them all
  // writes the list and returns the id it was written under, or undefined when it was not written: a church Sunday
  // left with nothing is deleted, or dropped when it was never saved (issue #4190)
  update(songList: SongList): Promise<number | undefined>;
  updateEach(songList: SongList[]): Promise<void>;
  updateRowIndices({
    rowIndex,
    offset,
    intervalTypes,
  }: {
    rowIndex: number;
    offset: number;
    intervalTypes?: IntervalType[];
  }): Promise<void>;
  containsUpdates: boolean;
}

const defaultTermlyCount = 1;
const defaultMonthlyCount = 2;
const defaultWeeklyCount = 5;

const defaultSchoolSongListSchema: SongListSchema = {
  rows: [
    ...createSchemaEntries({ count: defaultTermlyCount, offset: 0, intervalType: IntervalType.Custom }),
    ...createSchemaEntries({
      count: defaultMonthlyCount,
      offset: defaultTermlyCount,
      intervalType: IntervalType.Month,
    }),
    ...createSchemaEntries({
      count: defaultWeeklyCount,
      offset: defaultTermlyCount + defaultMonthlyCount,
      intervalType: IntervalType.Week,
    }),
  ],
};

export function getSchoolSongListSchema(school: Org | undefined, allSongLists: AllSongLists): SongListSchema {
  if (school?.type !== OrgType.School) {
    throw new Error(`Only schools may be passed to getSchoolSongListSchema`);
  }
  const schemaHasIntervalRows =
    (school?.churchLiturgySchema?.rows ?? []).filter(({ intervalType }) => intervalType !== undefined).length > 0;
  if (schemaHasIntervalRows && school.churchLiturgySchema) {
    return school.churchLiturgySchema;
  }
  const maxWeeklyIntervalRowIndex = Math.max(
    0,
    ...allSongLists
      .filter((songList) => hasInterval(songList) && songList.interval.type === IntervalType.Week)
      .map(({ songs }) => Math.max(0, ...songs.map(({ row }) => row))),
  );
  return maxWeeklyIntervalRowIndex > 0
    ? { rows: createSchemaEntries({ count: maxWeeklyIntervalRowIndex, offset: 0, intervalType: IntervalType.Week }) }
    : defaultSchoolSongListSchema;
}

function createSchemaEntries({
  count,
  offset,
  intervalType,
}: {
  count: number;
  offset: number;
  intervalType: IntervalType;
}): SongListSchemaEntry[] {
  return [...Array(count).keys()].map((index) => ({
    row: index + offset + 1,
    type: SongListEntryType.Song,
    intervalType,
    label: '',
  }));
}

// The list as the cache holds it now. The upload rewrites an id the server reassigned (a temporary id, or a church
// Sunday's retired id) in the cache, but a write can start from a render that still showed the old id: writing under
// the old id would store a second copy of the list beside the rewritten one (issue #4190).
function withCurrentId<T extends SongList>(songList: T): T {
  const id = songList.id === undefined ? undefined : mapSongListId(songList.id);
  return id === songList.id ? songList : { ...songList, id };
}

// One list per id, the later one winning: a shifted list mapped to its new id replaces a copy cached under that id.
function keepLastPerId(songLists: SongList[]): SongList[] {
  const lastIndexById = new Map(songLists.map(({ id }, index) => [id, index]));
  return songLists.filter(({ id }, index) => id === undefined || lastIndexById.get(id) === index);
}

// `songLists` as a write of `written` leaves them, for the passed-blank carry: each written list in place of the
// cached list with its id, and of the cached worship service of its date, and a worship service written with no entry
// (one the write deletes, or drops) gone
function withWritesApplied(songLists: SongList[], written: SongList[]): SongList[] {
  const ids = new Set(written.flatMap(({ id }) => (id === undefined ? [] : [id])));
  const serviceDates = new Set(
    written.flatMap((list) => (list.type === SongListType.WorshipService ? [list.date] : [])),
  );
  const replaced = (list: SongList) =>
    (list.id !== undefined && ids.has(list.id)) ||
    (list.type === SongListType.WorshipService && serviceDates.has(list.date));
  return [
    ...songLists.filter((list) => !replaced(list)),
    ...written.filter((list) => list.type !== SongListType.WorshipService || list.songs.length > 0),
  ];
}

// cannot useState because it lags behind when adding multiple song lists, so forced to use global (yuck!)
let nextSongListId = -1;

// Every write (update, updateEach and the row shifts) goes through normalizeChurchWeeklyListWrites, so a church's
// past Sunday never keeps a blank and a Sunday left with nothing loses its list, however the planner wrote it
// (issue #4190). An update and updateEach first carry forward the blank the housekeeping server moves off a passed
// Sunday (withCarriedPassedBlanks, issue #4192), so a cache that has not heard of the move cannot undo it by uploading
// the next Sunday whole. The carry reads the cache as the whole write leaves it, so a planner edit that writes a
// passed Sunday down in the same write (issue #4209, planner_edits.ts), moving its blank on or dropping it, is not
// undone by the carry reading the blank where it was. The row shifts leave the carry out: they renumber rows, and the
// next ordinary write carries it.
export function useAllSongLists(): AllSongLists {
  const [allSongLists, setAllSongLists] = useLocalStorage(LocalStorageKey.AllSongLists);
  const { currentOrg } = useCurrentOrg();
  const [containsUpdates, setContainsUpdates] = useState<boolean>(false);

  // the current Sunday as of the write, not as of the last render
  const normalizeWrites = useCallback(
    (songLists: SongList[]) => normalizeChurchWeeklyListWrites(currentOrg, thisSundayDate(todayDate()), songLists),
    [currentOrg],
  );

  // the lists of an update or updateEach, each with the passed Sundays' blanks carried forward, read from the cache
  // with all of the write's lists in place
  const carryPassedBlanks = useCallback(
    (songLists: SongList[]) => {
      const currentSunday = thisSundayDate(todayDate());
      const written = withWritesApplied(allSongLists, songLists);
      return songLists.map((songList) => withCarriedPassedBlanks(currentOrg, songList, written, currentSunday));
    },
    [allSongLists, currentOrg],
  );

  const markSongListUpdated = useCallback(async (songList: SongList, { isDeleted }: { isDeleted?: boolean } = {}) => {
    await recordSongListUpdate(songList, { isDeleted });
    void synchronizeAllSongLists();
  }, []);

  const allocateNewSongListId = useCallback((): number => {
    return nextSongListId--;
  }, []);

  useEffectOnce(() => {
    // a write-down mark waiting alone is unsaved too (issue #4209): the sync indicator shows, and switching org warns
    const listener = async () => setContainsUpdates(await songListSyncWorkPending());
    addSongListSyncListener(listener);
    return () => removeSongListSyncListener(listener);
  });

  const remove = useCallback(
    async (songList: SongList) => {
      if (!isDeletableSongListType(songList?.type)) {
        // only events, intervals, seasonal lists and church Sundays may be removed
        return;
      }
      const current = withCurrentId(songList);
      setAllSongLists((allSongLists) => allSongLists.filter(({ id }) => id !== songList.id && id !== current.id));
      await markSongListUpdated(current, { isDeleted: true });
    },
    [setAllSongLists, markSongListUpdated],
  );

  const updateRowIndices = useCallback(
    async ({
      rowIndex,
      offset,
      intervalTypes,
    }: {
      rowIndex: number;
      offset: number;
      intervalTypes?: IntervalType[];
    }) => {
      let changes: SongListChanges = { update: [], remove: [] };
      setAllSongLists((allSongLists) => {
        const updatedSongLists: SongList[] = [];
        const shiftedSongLists = allSongLists.map((cached) => {
          const current = withCurrentId(cached);
          const newSongList = shiftSongListRows(current, { rowIndex, offset, intervalTypes });
          if (!newSongList) {
            // a list the shift leaves alone stays as cached: a row shift rewrites no id
            return cached;
          }
          if (current.songs.find(({ row }) => row >= rowIndex)) {
            updatedSongLists.push(newSongList);
          }
          return newSongList;
        });
        // the stored lists match what is uploaded: a past church Sunday's blanks go here too
        changes = normalizeWrites(updatedSongLists);
        const writtenById = new Map(
          changes.update.filter(({ id }) => id !== undefined).map((songList) => [songList.id, songList]),
        );
        return keepLastPerId(shiftedSongLists.map((songList) => writtenById.get(songList.id) ?? songList));
      });
      const { update: written, remove: removed } = changes;
      await Promise.all(
        written.map(async (songList) => {
          await recordSongListUpdate(songList);
        }),
      );
      for (const songList of removed) {
        await remove(songList);
      }
      void synchronizeAllSongLists();
    },
    [normalizeWrites, remove, setAllSongLists],
  );

  const deleteRowAt = useCallback(
    async (rowIndex: number) => {
      await updateRowIndices({ rowIndex, offset: -1 });
    },
    [updateRowIndices],
  );

  const find = useCallback(
    (callback: (songList: SongList) => boolean) => {
      return allSongLists.find(callback);
    },
    [allSongLists],
  );

  const filter = useCallback(
    (callback: (songList: SongList) => boolean) => {
      return allSongLists.filter(callback);
    },
    [allSongLists],
  );

  const insertNewRowAt = useCallback(
    async (rowIndex: number) => {
      await updateRowIndices({ rowIndex, offset: 1 });
    },
    [updateRowIndices],
  );

  const map = useCallback(
    (callback: (songList: SongList) => any) => {
      return allSongLists.map(callback);
    },
    [allSongLists],
  );

  const toArray = useCallback(() => allSongLists, [allSongLists]);

  const update = useCallback(
    async (songList: SongList) => {
      const { update: written, remove: removed } = normalizeWrites(carryPassedBlanks([withCurrentId(songList)]));
      for (const list of removed) {
        await remove(list);
      }
      const [writtenList] = written;
      if (!writtenList) {
        return undefined;
      }
      writtenList.id = writtenList.id ?? allocateNewSongListId();
      setAllSongLists((allSongLists) => [...allSongLists.filter((list) => list.id !== writtenList.id), writtenList]);
      await markSongListUpdated(writtenList);
      return writtenList.id;
    },
    [allocateNewSongListId, carryPassedBlanks, markSongListUpdated, normalizeWrites, remove, setAllSongLists],
  );

  const updateEach = useCallback(
    async (songLists: SongList[]) => {
      const { update: written, remove: removed } = normalizeWrites(carryPassedBlanks(songLists.map(withCurrentId)));
      for (const songList of written) {
        songList.id = songList.id ?? allocateNewSongListId();
        setAllSongLists((allSongLists) => [...allSongLists.filter((list) => list.id !== songList.id), songList]);
        await recordSongListUpdate(songList);
      }
      for (const songList of removed) {
        await remove(songList);
      }
      void synchronizeAllSongLists();
    },
    [allocateNewSongListId, carryPassedBlanks, normalizeWrites, remove, setAllSongLists],
  );

  const addCustomInterval = useCallback(
    async ({ start, end }: { start: DateString; end: DateString }): Promise<number> => {
      const songList: IntervalSongList & { id: number } = {
        id: allocateNewSongListId(),
        type: SongListType.Interval,
        interval: {
          type: IntervalType.Custom,
          start,
          end,
        },
        songs: [],
      };
      setAllSongLists((allSongLists) => [...allSongLists, songList]);
      await markSongListUpdated(songList);
      return songList.id;
    },
    [allocateNewSongListId, markSongListUpdated, setAllSongLists],
  );

  return {
    addCustomInterval,
    containsUpdates,
    deleteRowAt,
    find,
    filter,
    insertNewRowAt,
    map,
    remove,
    toArray,
    update,
    updateEach,
    updateRowIndices,
  };
}
